import Wishlist from '@theme/wishlist';

/**
 * @typedef {import('./fly-to-cart.js').default} FlyToCart
 */

/**
 * `<wishlist-button>` — a heart toggle that saves/unsaves a product.
 *
 * The button markup is rendered in Liquid (progressive enhancement); this
 * element wires the behaviour, keeps its pressed/active state in sync with the
 * shared `Wishlist` store, and — when saving — flies a thumbnail of the artwork
 * up to the header wishlist icon by reusing the theme's `<fly-to-cart>` element.
 */
class WishlistButton extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** @type {boolean} */
  #animating = false;

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.productImage = this.dataset.productImage || null;

    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector('.wishlist-button__btn');
    /** @type {HTMLElement | null} */
    this.icon = this.querySelector('.wishlist-button__icon');

    this.addEventListener('click', this.#onClick);
    this.#unsubscribe = Wishlist.subscribe(() => this.#syncState());
    this.#syncState();
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    // Cards wrap the whole tile in an anchor; keep the click on the heart.
    event.preventDefault();
    event.stopPropagation();

    if (!this.productId) return;

    const wasSaved = Wishlist.isSaved(this.productId);
    const nowSaved = Wishlist.toggle(this.productId);

    this.#animateIcon();
    if (nowSaved && !wasSaved) this.#flyToWishlist();
  };

  /** Reflects the current saved state onto the button (class, aria, label). */
  #syncState() {
    if (!this.productId) return;

    const saved = Wishlist.isSaved(this.productId);
    this.classList.toggle('wishlist-button--active', saved);

    if (this.button) {
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }

    const labelText = this.querySelector('.wishlist-button__label');
    if (labelText) {
      labelText.textContent = saved ? this.dataset.textSaved || '' : this.dataset.textSave || '';
    }
  }

  /** Brief pop when toggled. */
  #animateIcon() {
    if (!this.icon || this.#animating) return;
    this.#animating = true;
    this.icon.classList.add('wishlist-button__icon--animating');
    setTimeout(() => {
      this.icon?.classList.remove('wishlist-button__icon--animating');
      this.#animating = false;
    }, 300);
  }

  /** Flies a thumbnail of the artwork to the header wishlist icon. */
  #flyToWishlist() {
    const destination = document.querySelector('.header-actions__wishlist-icon');
    if (!destination || !this.productImage) return;

    const flyElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    flyElement.classList.add('fly-to-cart--main');
    flyElement.style.setProperty('background-image', `url(${this.productImage})`);
    flyElement.style.setProperty('--start-opacity', '0');
    flyElement.source = this.button || this;
    flyElement.destination = destination;

    document.body.appendChild(flyElement);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

export default WishlistButton;
